import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { availableLocales, translations, type LocaleKey } from '../i18n';

type I18nContextValue = {
  locale: LocaleKey;
  setLocale: (next: LocaleKey) => void;
  t: (key: string) => string;
  locales: typeof availableLocales;
};

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

const getInitialLocale = (): LocaleKey => {
  try {
    const stored = localStorage.getItem('locale');
    if (stored === 'tr' || stored === 'en') {
      return stored;
    }
  } catch {
    // ignore
  }
  return 'tr';
};

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [locale, setLocaleState] = useState<LocaleKey>(getInitialLocale);

  const setLocale = useCallback((next: LocaleKey) => {
    setLocaleState(next);
    try {
      localStorage.setItem('locale', next);
    } catch {
      // ignore
    }
  }, []);

  const t = useCallback(
    (key: string) => {
      const current = translations[locale]?.[key];
      if (current) return current;
      const fallback = translations.tr?.[key];
      return fallback || key;
    },
    [locale],
  );

  const value = useMemo(
    () => ({
      locale,
      setLocale,
      t,
      locales: availableLocales,
    }),
    [locale, setLocale, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return ctx;
};
